/**
 * PortalGoalsPage — Phase 6.
 *
 * Read-only portal view of approved active goals + their outcome history.
 * Surfaces only goals the coordinator has explicitly published via the
 * "Publish to Portal" toggle. No edits, no comments — pure visibility.
 *
 * Backend: GET /api/portal/participant/goals and /outcomes (both gated by
 * `portal_goals_visibility_enabled`). When the flag is OFF the endpoints
 * return 404; this page surfaces a graceful "not available" state.
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import portalApi, { getPortalErrorMessage } from '@/lib/portalApi';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Target, RefreshCw, Award, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

export default function PortalGoalsPage() {
  const [loading, setLoading] = useState(true);
  const [goals, setGoals] = useState([]);
  const [outcomesByGoal, setOutcomesByGoal] = useState({});
  const [summary, setSummary] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setUnavailable(false);
    try {
      const g = await portalApi.get('/portal/participant/goals');
      const list = g.data.data?.goals || [];
      setGoals(list);
      const o = await portalApi.get('/portal/participant/outcomes');
      const outcomes = o.data.data?.outcomes || [];
      const grouped = {};
      outcomes.forEach((row) => {
        const k = String(row.goal_id);
        (grouped[k] = grouped[k] || []).push(row);
      });
      setOutcomesByGoal(grouped);
      setSummary(o.data.data?.summary || null);
    } catch (e) {
      if (e?.response?.status === 404) {
        setUnavailable(true);
      } else {
        toast.error(getPortalErrorMessage(e) || 'Failed to load goals');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalOutcomes = useMemo(
    () => Object.values(outcomesByGoal).reduce((acc, arr) => acc + arr.length, 0),
    [outcomesByGoal],
  );

  if (unavailable) {
    return (
      <div className="p-6 max-w-3xl mx-auto space-y-4" data-testid="portal-goals-unavailable">
        <h1 className="text-2xl font-bold">Goals</h1>
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            Goals are not yet available on the portal for your account.
            Please contact your support coordinator.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-4" data-testid="portal-goals-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" /> Goals
          </h1>
          <p className="text-sm text-muted-foreground">
            Your approved goals and outcome history.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}
                data-testid="portal-goals-refresh">
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {summary && summary.total_outcomes > 0 && (
        <Card data-testid="portal-goals-summary">
          <CardContent className="p-4 grid grid-cols-3 gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Outcomes recorded</p>
              <p className="text-xl font-semibold" data-testid="portal-goals-summary-total">{summary.total_outcomes}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Avg achievement</p>
              <p className="text-xl font-semibold" data-testid="portal-goals-summary-avg">{summary.achievement_avg_pct}%</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Achieved rate</p>
              <p className="text-xl font-semibold" data-testid="portal-goals-summary-rate">{summary.achievement_rate_pct}%</p>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <Card>
          <CardContent className="space-y-2 p-4">
            <Skeleton className="h-5 w-40" /><Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </CardContent>
        </Card>
      ) : goals.length === 0 ? (
        <Card data-testid="portal-goals-empty">
          <CardContent className="py-10 text-center text-muted-foreground">
            No goals have been shared with you on the portal yet.
          </CardContent>
        </Card>
      ) : (
        goals.map((g) => {
          const ths = outcomesByGoal[g.id] || [];
          const isOpen = expanded === g.id;
          return (
            <Card key={g.id} data-testid={`portal-goal-card-${g.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      {g.title}
                      <Badge variant="outline" className="text-[10px]">{g.category || g.status}</Badge>
                    </CardTitle>
                    {g.description && (
                      <CardDescription className="mt-1">{g.description}</CardDescription>
                    )}
                  </div>
                  <Button size="sm" variant="ghost"
                          onClick={() => setExpanded(isOpen ? null : g.id)}
                          data-testid={`portal-goal-toggle-${g.id}`}>
                    {ths.length} outcome{ths.length === 1 ? '' : 's'}
                    <ChevronRight className={`h-3.5 w-3.5 ml-1 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                  </Button>
                </div>
              </CardHeader>
              {(g.specific || g.measurable || g.time_bound) && (
                <CardContent className="text-sm grid grid-cols-1 md:grid-cols-3 gap-2">
                  {g.specific && <div><p className="text-[10px] uppercase tracking-wide text-muted-foreground">Specific</p><p>{g.specific}</p></div>}
                  {g.measurable && <div><p className="text-[10px] uppercase tracking-wide text-muted-foreground">Measurable</p><p>{g.measurable}</p></div>}
                  {g.time_bound && <div><p className="text-[10px] uppercase tracking-wide text-muted-foreground">Time-bound</p><p>{g.time_bound}</p></div>}
                </CardContent>
              )}
              {isOpen && ths.length > 0 && (
                <CardContent className="pt-0" data-testid={`portal-goal-outcomes-${g.id}`}>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Outcome history</p>
                  <ul className="text-sm space-y-1">
                    {ths.map((o) => (
                      <li key={o.id} className="flex items-center justify-between border-t py-1.5">
                        <span className="flex items-center gap-2">
                          <Award className="h-3 w-3 text-amber-500" />
                          {String(o.recorded_at).slice(0, 10)} ·
                          <Badge variant="outline" className="text-[10px]">{o.measurement_type}</Badge>
                        </span>
                        <span className="font-semibold">{o.achievement_pct ?? '—'}%</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              )}
            </Card>
          );
        })
      )}

      {totalOutcomes > 0 && (
        <p className="text-[10px] text-center text-muted-foreground" data-testid="portal-goals-readonly-note">
          Read-only view · Coordinator-published content
        </p>
      )}
    </div>
  );
}
